import React from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getEmployees } from '@/api/employees';
import { useAuth } from '@/contexts/AuthContext';
import type { Employee } from '@/types/resources/employee';

interface AssignedEmployeeSelectorProps {
  disabled?: boolean;
}

export const AssignedEmployeeSelector: React.FC<AssignedEmployeeSelectorProps> = ({
  disabled = false
}) => {
  const { t } = useTranslation(['employees', 'common', 'tasks']);
  const { firestoreUser } = useAuth();

  // Fetch employees
  const { data: employeesData, error } = useQuery({
    queryKey: ['employees', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getEmployees({ 
        firestoreUser,
        pageSize: 100 // Get all employees for selector
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const employeeOptions = employeesData?.employees?.map((employee: Employee) => ({
    value: employee.id,
    label: employee.user.displayName,
  })) || [];

  if (error) {
    return (
      <>
        <Form.Item
          name="assignedTo"
          label={t('tasks:assignedTo')}
          rules={[{ required: true, message: t('tasks:assignedToRequired') }]}
        >
          <Select
            placeholder={t('employees:fields.selectEmployee')}
            disabled={true}
            options={[]}
          />
        </Form.Item>
        <Form.Item
          name="assignedBy"
          label={t('tasks:assignedBy', 'Assigned By')}
          rules={[{ required: true, message: t('tasks:assignedByRequired', 'Assigned By is required') }]}
        >
          <Select
            placeholder={t('employees:fields.selectEmployee')}
            disabled={true}
            options={[]}
          />
        </Form.Item>
      </>
    );
  }

  return (
    <>
      <Form.Item
        name="assignedTo"
        label={t('tasks:assignedTo')}
        rules={[{ required: true, message: t('tasks:assignedToRequired') }]}
      >
        <Select
          placeholder={t('employees:fields.selectEmployee')}
          disabled={disabled}
          allowClear
          options={employeeOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>
      <Form.Item
        name="assignedBy"
        label={t('tasks:assignedBy', 'Assigned By')}
        rules={[{ required: true, message: t('tasks:assignedByRequired', 'Assigned By is required') }]}
      >
        <Select
          placeholder={t('employees:fields.selectEmployee')}
          disabled={disabled}
          allowClear
          options={employeeOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      </Form.Item>
    </>
  );
};

export default AssignedEmployeeSelector; 